import { m } from "@plannello/i18n/messages";
import { getLocale } from "@plannello/i18n/runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Form } from "react-aria-components";
import { AuthCard } from "@/components/shell/AuthCard";
import { Button, buttonStyles, InlineError, PasswordField, Select, Skeleton, TextField } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { clearDrafts, useDraft } from "@/lib/draft";
import { focusFirstInvalid } from "@/lib/forms";
import { applyLocale, LOCALES } from "@/lib/locale";
import { type Locale, loadSetupStatus, meQuery, setupStatusQuery } from "@/lib/queries";
import { describeError } from "@/lib/session";

export const Route = createFileRoute("/setup")({
  beforeLoad: async ({ context }) => {
    const status = await loadSetupStatus(context.queryClient);
    if (status && !status.needsSetup) throw redirect({ to: "/login", search: { redirect: undefined } });
  },
  component: SetupPage,
});

type FieldName = "instanceName" | "defaultLocale" | "name" | "email" | "password" | "setupToken";
type Errors = Partial<Record<FieldName | "form", string>>;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SetupPage() {
  const router = useRouter();
  const qc = useQueryClient();
  const status = useQuery(setupStatusQuery);

  const [instanceName, setInstanceName] = useDraft("setup.instanceName", "");
  const [language, setLanguage] = useDraft<Locale>("setup.language", getLocale() as Locale);
  const [name, setName] = useDraft("setup.name", "");
  const [email, setEmail] = useDraft("setup.email", "");
  const [password, setPassword] = useDraft("setup.password", "");
  // The one-time code from the server log; kept out of the persisted drafts on purpose.
  const [setupToken, setSetupToken] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [pending, setPending] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);

  async function submit() {
    const next: Errors = {};
    if (!instanceName.trim()) next.instanceName = m.validation_required();
    if (!name.trim()) next.name = m.validation_required();
    if (!email.trim()) next.email = m.validation_required();
    else if (!EMAIL.test(email.trim())) next.email = m.validation_email();
    if (!password) next.password = m.validation_required();
    if (!setupToken.trim()) next.setupToken = m.validation_required();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      focusFirstInvalid();
      return;
    }

    setPending(true);
    try {
      await api.post("/api/v1/setup", {
        instanceName: instanceName.trim(),
        defaultLocale: language,
        name: name.trim(),
        email: email.trim(),
        password,
        setupToken: setupToken.trim(),
      });
      clearDrafts("setup.");
      qc.removeQueries({ queryKey: meQuery.queryKey });
      await qc.invalidateQueries({ queryKey: setupStatusQuery.queryKey });
      await router.navigate({ to: "/" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 403 && e.code === "setup_token_invalid") {
        setErrors({ setupToken: m.setup_code_invalid() });
        focusFirstInvalid();
      } else if (e instanceof ApiError && e.status === 409) {
        setAlreadyDone(true);
        setErrors({});
      } else if (e instanceof ApiError && e.status === 400) {
        const fields: Errors = {};
        for (const [k, v] of Object.entries(e.fields ?? {})) fields[k as FieldName] = v;
        if (Object.keys(fields).length === 0) fields.form = e.message || m.error_generic();
        setErrors(fields);
      } else {
        setErrors({ form: describeError(e, { network: m.error_network(), generic: m.error_generic() }) });
      }
    } finally {
      setPending(false);
    }
  }

  if (status.isPending) {
    return (
      <AuthCard title={m.setup_title()}>
        <div role="status" aria-busy="true" className="flex flex-col gap-4">
          <span className="sr-only">{m.common_loading()}</span>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      </AuthCard>
    );
  }

  if (status.isError) {
    return (
      <AuthCard title={m.setup_title()}>
        <InlineError
          message={describeError(status.error, { network: m.error_network(), generic: m.error_generic() })}
          onRetry={() => void status.refetch()}
          isRetrying={status.isFetching}
        />
      </AuthCard>
    );
  }

  if (alreadyDone || !status.data.needsSetup) {
    return (
      <AuthCard title={m.setup_done_title()} intro={m.setup_done_body()}>
        <Link
          to="/login"
          search={{ redirect: undefined }}
          className={buttonStyles({ variant: "primary", size: "lg", className: "w-full" })}
        >
          {m.auth_sign_in()}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={m.setup_title()} intro={m.setup_intro()}>
      <Form
        validationBehavior="aria"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {errors.form ? <InlineError message={errors.form} /> : null}
        <TextField
          label={m.setup_code()}
          name="setupToken"
          autoComplete="off"
          autoFocus
          spellCheck="false"
          value={setupToken}
          onChange={setSetupToken}
          error={errors.setupToken}
          description={m.setup_code_help()}
        />
        <TextField
          label={m.setup_instance_name()}
          name="instanceName"
          autoComplete="organization"
          value={instanceName}
          onChange={setInstanceName}
          error={errors.instanceName}
          placeholder={m.setup_instance_placeholder()}
        />
        <Select
          id="setup-language"
          label={m.setup_language()}
          name="defaultLocale"
          items={LOCALES.map((l) => ({ id: l.value, label: l.label }))}
          selectedKey={language}
          onSelectionChange={(k) => {
            setLanguage(k as Locale);
            applyLocale(k, { refocusId: "setup-language" });
          }}
          error={errors.defaultLocale}
        />
        <hr className="-mx-6 my-1 border-line" />
        <TextField
          label={m.auth_name()}
          name="name"
          autoComplete="name"
          value={name}
          onChange={setName}
          error={errors.name}
        />
        <TextField
          label={m.auth_email()}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={setEmail}
          error={errors.email}
        />
        <PasswordField
          label={m.auth_password()}
          name="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          error={errors.password}
        />
        <Button type="submit" variant="primary" size="lg" isPending={pending} className="mt-2 w-full">
          {m.setup_create()}
        </Button>
      </Form>
    </AuthCard>
  );
}
